import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRound, Bot, AlertTriangle, CalendarCheck, CalendarClock, CalendarX } from 'lucide-react';

// Shared cell renderers so the calls table looks identical everywhere it's shown
// (the /calls page and the SUPERDOC home "Recent calls" widget).

/** Fixed column set + order for any calls table. */
export const CALL_COLUMN_ORDER = [
  'startedAt',
  'callerName',
  'callerPhone',
  'durationSec',
  'booked',
  'endedReason',
] as const;

/** How a call ended, with an at-a-glance icon: who hung up (customer/assistant)
 *  or an error. The error tint lives on THIS cell only — rows are never tinted. */
export function EndedReasonCell({ value }: { value: unknown }) {
  const v = String(value ?? '');
  if (!v) return <span className="text-muted-foreground">—</span>;
  let icon: ReactNode = null;
  let badge = 'bg-muted text-muted-foreground';
  if (v === 'customer-ended-call') {
    icon = <UserRound className="h-3.5 w-3.5 shrink-0" />;
    badge = 'bg-sky-500/10 text-sky-600';
  } else if (v === 'assistant-ended-call') {
    icon = <Bot className="h-3.5 w-3.5 shrink-0" />;
    badge = 'bg-primary/10 text-primary';
  } else if (v.includes('error') || v === 'failure') {
    icon = <AlertTriangle className="h-3.5 w-3.5 shrink-0" />;
    badge = 'bg-destructive/10 text-destructive';
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      {icon ? <span className={`rounded p-1 ${badge}`}>{icon}</span> : null}
      <span>{v}</span>
    </span>
  );
}

/** Whether a booking was actually made during the call. */
export function BookedCell({ value }: { value: unknown }) {
  const { t } = useTranslation();
  return value ? (
    <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
      <CalendarCheck className="h-3 w-3 shrink-0" />
      {t('calls.bookedYes')}
    </span>
  ) : (
    <span className="text-muted-foreground">{t('calls.bookedNo')}</span>
  );
}

/** What the call did to a booking: booked / rescheduled / cancelled / none.
 *  Reads `Call.bookingAction`, falling back to the legacy `booked` bool for rows
 *  recorded before the action was tracked. */
export function CallBookingBadge({ action, booked }: { action?: unknown; booked?: unknown }) {
  const { t } = useTranslation();
  const state = (typeof action === 'string' ? action : null) ?? (booked ? 'BOOKED' : null);
  if (state === 'BOOKED') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
        <CalendarCheck className="h-3 w-3 shrink-0" />
        {t('calls.booking.booked')}
      </span>
    );
  }
  if (state === 'RESCHEDULED') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-medium text-amber-600">
        <CalendarClock className="h-3 w-3 shrink-0" />
        {t('calls.booking.rescheduled')}
      </span>
    );
  }
  if (state === 'CANCELLED') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[11px] font-medium text-destructive">
        <CalendarX className="h-3 w-3 shrink-0" />
        {t('calls.booking.cancelled')}
      </span>
    );
  }
  return <span className="text-muted-foreground">{t('calls.booking.none')}</span>;
}

/** Caller name / phone as plain text — the row click opens the call detail. */
export function CallerCell({ by, value }: { by: 'name' | 'phone'; value: unknown }) {
  const v = value == null ? '' : String(value);
  if (!v) return <span className="text-muted-foreground">—</span>;
  return <span className={by === 'phone' ? 'tabular-nums' : ''}>{v}</span>;
}
